import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Eye, X, Calendar, MapPin } from 'lucide-react';
import './Portfolio.css';
import Chatbot from '../components/Chatbot';

// Helper to load dynamic user uploaded projects
const getCombinedProjects = (defaultProjects) => {
  try {
    const custom = JSON.parse(localStorage.getItem('sakae_custom_projects') || '[]');
    return [...custom.filter(p => p.featured !== false), ...defaultProjects];
  } catch (e) {
    return defaultProjects;
  }
};

const formatSectionTitle = (key) => key
  .replace(/([A-Z])/g, ' $1')
  .replace(/^./, (letter) => letter.toUpperCase());

// 3D Tilt hook for realistic card depth
const useTilt = () => {
  const ref = useRef(null);
  const onMouseMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width  - 0.5;
    const y = (e.clientY - rect.top)  / rect.height - 0.5;
    el.style.transform = `perspective(900px) rotateY(${x * 12}deg) rotateX(${-y * 10}deg) scale(1.03)`;
    el.style.boxShadow = `${-x * 20}px ${y * 20}px 50px rgba(0,0,0,0.25), 0 0 0 1px rgba(212,175,55,0.30)`;
  }, []);
  const onMouseLeave = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.transform = 'perspective(900px) rotateY(0deg) rotateX(0deg) scale(1)';
    el.style.boxShadow = '';
    el.style.transition = 'transform 0.6s ease, box-shadow 0.6s ease';
  }, []);
  return { ref, onMouseMove, onMouseLeave };
};

const TiltCard = ({ project, onOpen }) => {
  const { ref, onMouseMove, onMouseLeave } = useTilt();
  return (
    <div
      ref={ref}
      className="reveal-depth group relative h-80 rounded-2xl overflow-hidden cursor-pointer shadow-lg will-change-transform transition-all duration-300"
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      onClick={() => onOpen(project)}
    >
      <img
        src={project.image}
        alt={project.title}
        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/90 via-emerald-950/40 to-transparent opacity-80 group-hover:opacity-95 transition-opacity duration-300"></div>
      <div className="absolute bottom-5 left-5 right-5 text-white">
        <span className="inline-block text-xs font-semibold tracking-widest text-amber-400 uppercase mb-1">
          {project.category}
        </span>
        <h3 className="text-xl font-bold font-serif leading-snug">{project.title}</h3>
        <div className="flex items-center gap-3 mt-2 text-xs text-gray-300">
          <span>{project.year}</span>
          <span>•</span>
          <span>{project.area}</span>
        </div>
      </div>
      <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="bg-amber-400 p-2.5 rounded-full shadow-lg text-gray-900 transform group-hover:scale-110 transition-transform">
          <Eye size={18} />
        </div>
      </div>
    </div>
  );
};

const Portfolio = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const projects = getCombinedProjects([
    // Project 1 - Office Architecture by Sakae Studio
    // {
    //   id: 5,
    //   title: 'Office Architecture by Sakae Studio',
    //   category: 'Commercial',
    //   architect: 'Sameed Ahmed Khan',
    //   projectType: 'Office Building',
    //   strategy: 'Comprehensive Design',
    //   date: 'October 2024',
    //   status: 'Ongoing',
    //   image: require('../assets/OfficeComplex/o1.webp'),
    //   description: 'A seamless dialogue between nature and structure, redefining the modern workspace experience with a central open courtyard, bold glass façade, and vertical greens.',
    //   details: {
    //     overview: 'Envisioned as a statement of contemporary architecture and refined functionality, this office complex by Sakae Studio embraces a central open courtyard that draws in natural light and greenery to create calm, breathable interiors.',
    //     architecture: [
    //       'Bold glass façade framed with copper-toned metal fins',
    //       'Vertical greens integrated throughout the facade',
    //       'Central open courtyard for natural light and ventilation',
    //       'Landscaped forecourt with water feature',
    //       'Metal lattice panels creating visual rhythm',
    //       'Philosophy of blending opulence with restraint'
    //     ],
    //     sitePlanning: [
    //       'Efficient circulation and movement patterns',
    //       'Clear zoning for parking and pedestrian areas',
    //       'Landscape integration throughout the site',
    //       'Carefully choreographed approach sequence',
    //       'Visual transparency and openness',
    //       'Sense of grandeur with functional simplicity'
    //     ],
    //     interiorSpaces: [
    //       'Calm and breathable working environments',
    //       'Natural light throughout the building',
    //       'Visual openness centered around courtyard',
    //       'Inspiring and efficient workspace design',
    //       'Clarity in circulation and ease of access',
    //       'Spatial comfort and modern aesthetics'
    //     ],
    //     designPhilosophy: [
    //       'Crafted modernism with timeless elegance',
    //       'Balance between modern aesthetics and sustainability',
    //       'Every detail speaks of refined craftsmanship',
    //       'Facade articulation with geometric precision',
    //       'Landscape geometry integration',
    //       'Embodiment of Opulent Architecture'
    //     ]
    //   },
    //   client: 'Corporate Client',
    //   year: '2024',
    //   area: '15,000 sq ft',
    //   gallery: Array.from({length: 16}, (_, i) =>
    //     require(`../assets/OfficeComplex/o${i+1}.webp`)
    //   ),
    //   designHighlights: [
    //     'Central landscaped courtyard design',
    //     'Bold glass façade with metal lattice',
    //     'Vertical green integration',
    //     'Natural light optimization',
    //     'Efficient circulation planning',
    //     'Contemporary sustainable architecture'
    //   ]
    // },
    // Project 1 - Odisha Enclave
    {
      id: 4,
      title: 'Odisha Enclave',
      category: 'Residential',
      architect: 'Sameed Ahmed Khan',
      projectType: 'Apartment',
      strategy: 'Comprehensive Design',
      date: 'May 2025',
      status: 'Completed',
      image: require('../../src/assets/OdishaEnclave/d1.webp'),
      description: 'Innovative interior design focusing on creating modern, sophisticated, and inviting living spaces with a blend of contemporary aesthetics and functional elegance.',
      details: {
        overview: 'Odisha Enclave showcases innovative interior design, emphasizing a blend of contemporary aesthetics with functional elegance through detailed mood boards and high-quality renders.',
        conceptualization: [
          'Comprehensive mood board capturing space essence and ambiance',
          'Sophisticated color palettes with neutrals, greys, and whites',
          'Warm wood tones with subtle accents of black and muted blues',
          'Luxurious textures including marble, polished concrete, and light wood',
          'Modern furniture designs with statement lighting fixtures',
          'Abstract art pieces for contemporary elegance'
        ],
        livingSpaces: [
          'Open-plan living and dining with spacious layouts',
          'Elegant furnishings with light neutral-toned sofas',
          'Modern coffee tables with marble tops and sleek designs',
          'Contemporary dining tables with comfortable upholstered chairs',
          'Sophisticated multi-layered lighting with recessed spotlights',
          'Decorative pendant lamps enhancing ambiance'
        ],
        designElements: [
          'Feature walls with vertical fluting and subtle paneling',
          'Wooden flooring and accents bringing warmth',
          'Light wall colors creating harmonious balance',
          'Strategically placed modern artwork and decorative elements',
          'Wall-mounted televisions in stylish media consoles',
          'Integrated shelving units providing entertainment and storage'
        ],
        bedrooms: [
          'Continuation of modern aesthetic into private spaces',
          'Emphasis on comfort and sophisticated simplicity',
          'Large integrated display units for design continuity',
          'Luxurious and functional bedroom environments',
          'Sophisticated storage solutions integration'
        ]
      },
      client: 'Private Client',
      year: '2025',
      area: '1800 sq ft',
      gallery: Array.from({length: 2}, (_, i) =>
        require(`../../src/assets/OdishaEnclave/d${i+1}.webp`)
      ),
      designHighlights: [
        'Contemporary aesthetics with functional elegance',
        'Sophisticated color palettes and material selection',
        'Multi-layered lighting design',
        'Integrated entertainment and storage solutions',
        'Modern furniture and statement pieces',
        'Cohesive design continuity throughout spaces'
      ]
    },
    // Project 2 - DD Residence
    {
      id: 3,
      title: 'Skyline Crest Apartments',
      category: 'commercial',
      architect: 'Sameed Ahmed Khan',
      projectType: 'High-Rise Residential Development',
      strategy: 'Comprehensive Design',
      date: 'February 2024',
      status: 'Completed',
      image: require('../../src/assets/SkylineCrest/c1.webp'),
      description: 'A comprehensive high-rise residential development featuring master planning with extensive amenities, detailed unit designs, and complete infrastructure for modern community living.',
      details: {
        overview: 'Skyline Crest Apartments showcases comprehensive architectural and interior design expertise from conceptualization to detailed execution in high-rise residential development.',
        masterPlanning: [
          'Large-scale residential development with multiple blocks (A1-A7, B1-B5)',
          'Wide roads (8M and 10M) with strategic planning',
          'Community amenities including parks and play areas',
          'Amphitheaters and clubhouses with social spaces',
          'Swimming pools and comprehensive sports complexes',
          'Commercial zones and essential infrastructure integration'
        ],
        unitDesign: [
          '2BHK units with living area, kitchen, dining space, and balconies',
          '3BHK units with family living/dining and drawing rooms',
          'Bright living spaces with light-colored walls and marble flooring',
          'Sophisticated lighting with cove lighting and elegant chandeliers',
          'Plush furnishings with dark ornate rugs and wooden accents',
          'Integrated entertainment units and strategic hidden storage'
        ],
        bedrooms: [
          'Master bedrooms with dark textured headboard walls',
          'Integrated lighting and striking floor-to-ceiling wardrobes',
          'Study areas with shelving and task lighting',
          'Warm wooden tones with ample storage solutions',
          'Sleek and minimalist vanity areas'
        ],
        kitchens: [
          'Modern and highly functional design approach',
          'Black marble countertops with olive green base cabinets',
          'Crisp white upper cabinets for visual contrast',
          'Optimized storage and abundant natural light',
          'Striking color palettes for visual appeal'
        ],
        amenities: [
          'Multi-level club house for social and recreational activities',
          'Kindergarten with functional child-friendly environment',
          'Restaurant with well-laid-out dining and kitchen areas',
          'Sports complexes (volleyball, tennis, basketball, cricket pitch)',
          'Swimming pools and comprehensive recreational facilities'
        ]
      },
      client: 'Real Estate Developer',
      year: '2024',
      area: '25 acres',
      gallery: Array.from({length: 8}, (_, i) =>
        require(`../../src/assets/SkylineCrest/c${i+1}.webp`)
      ),
      designHighlights: [
        'Master planning with extensive amenities',
        'Multi-block residential development',
        'Comprehensive sports and recreation facilities',
        'Modern 2BHK and 3BHK unit designs',
        'Club house and community spaces',
        'Integrated electrical and MEP planning'
      ]
    },
    // Project 3 - 3BHK Luxury Apartment
    {
      id: 1,
      title: '3BHK Luxury Apartment',
      category: 'Residential',
      architect: 'Sameed Ahmed Khan',
      projectType: '3BHK Apartment',
      strategy: 'Warm, Comfortable & Functional Elegance',
      date: 'February 2025',
      status: 'Completed',
      image: require('../../src/assets/apartment/a1.webp'),
      description: 'A sophisticated 3BHK apartment featuring modern amenities, luxurious finishes, and thoughtful space planning that combines elegance with functionality.',
      details: {
        overview: 'This residential project showcases our ability to create modern, functionally elegant spaces through thoughtful planning and sophisticated material choices.',
        livingDining: [
          'Open-plan layout with light walls and marble floors',
          'Varied lighting scheme for bright, inviting atmosphere',
          'Elegant furnishings with rich rugs and wooden accents',
          'Integrated entertainment system with hidden wiring',
          'Custom storage solutions throughout',
          'Contemporary art pieces as focal points'
        ],
        bedrooms: [
          'Master bedroom with dark, lit headboard feature wall',
          'Black glass wardrobes with interior lighting',
          'Integrated study areas with warm wood tones',  
          'Customized storage solutions for each family member',
          'Sleek workspace with extensive shelving',
          'Minimalist vanity areas with smart storage'
        ],
        kitchen: [
          'Black marble countertops with waterfall edge',
          'Vibrant green and white high-gloss cabinetry',
          'Integrated high-end appliances',
          'Optimized storage with pull-out systems',
          'Abundant natural light from skylights',
          'Practical yet visually striking design'
        ]
      },
      client: 'Private Client',
      year: '2025',
      area: '1200 sq ft',
      gallery: Array.from({length: 14}, (_, i) =>
        require(`../../src/assets/apartment/a${i+1}.webp`)
      ),
      designHighlights: [
        'Functional elegance with premium materials',
        'Custom storage solutions throughout',
        'Lighting-designed atmosphere variations',
        'Smart home technology integration',
        'Bespoke furniture pieces',
        'Material contrasts for visual interest'
      ]
    },
  ]);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % projects.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + projects.length) % projects.length);
  };

  const openModal = (project) => {
    setSelectedProject(project);
    setActiveImageIndex(0);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedProject(null);
    document.body.style.overflow = 'auto';
  };

  const nextImage = () => {
    setActiveImageIndex(prev =>
      prev === selectedProject.gallery.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setActiveImageIndex(prev =>
      prev === 0 ? selectedProject.gallery.length - 1 : prev - 1
    );
  };

  return (
    <>
      <section
        id="portfolio"
        className="py-24 relative text-white overflow-hidden"
        style={{
          backgroundColor: '#021a13',
          background: 'linear-gradient(180deg, #021a13 0%, #03261b 50%, #021a13 100%)',
          color: "#ffffff"
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16 reveal-left">
            <p className="font-serif text-xs uppercase tracking-widest text-amber-400 mb-2">Featured Renders & Projects</p>
            <h2 className="text-4xl md:text-5xl font-serif font-normal text-white mb-4">
              Our Portfolio
            </h2>
            <div className="w-20 h-0.5 bg-gradient-to-r from-amber-600 via-amber-400 to-amber-600 mx-auto mb-4"></div>
            <p className="text-base text-gray-300 max-w-2xl mx-auto">
              Discover our latest interior design projects and 3D spatial transformations
            </p>
          </div>

     {/* Mobile Stack */}
<div className="md:hidden space-y-6">
  {projects.map((project, index) => (
    <div
      key={index}
      className="relative h-80 rounded-2xl overflow-hidden cursor-pointer"
      onClick={() => openModal(project)}
    >
      <img
        src={project.image}
        alt={project.title}
        className="w-full h-full object-cover"
        onError={(e) => e.target.src = '/placeholder-image.jpg'}
      />
      <div className="absolute inset-0 bg-gradient-to-t gradient-overlay"></div>
      <div className="absolute bottom-4 left-4 text-white">
        <h3 className="text-xl font-bold">{project.title}</h3>
        <p className="text-amber-300">{project.category}</p>
      </div>
      <div className="absolute top-4 right-4">
        <div className="bg-white/80 p-2 rounded-full">
          <Eye size={18} color="#333" />
        </div>
      </div>
    </div>
  ))}
</div>
          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <TiltCard key={index} project={project} onOpen={openModal} />
            ))}
          </div>
        </div>
      </section>

      {/* Project Modal */}
      {selectedProject && (
        <div className="project-modal-overlay" onClick={closeModal}>
          <div className="project-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal" onClick={closeModal}>
              <X size={24} />
            </button>

            <div className="modal-header">
              <h2>{selectedProject.title}</h2>
              <div className="project-meta">
                <span>{selectedProject.projectType}</span>
                <span>{selectedProject.area}</span>
                <span>{selectedProject.year}</span>
              </div>
            </div>

            <div className="gallery-section">
              <div className="main-image">
                <img
                  src={selectedProject.gallery[activeImageIndex]}
                  alt={`${selectedProject.title} - View ${activeImageIndex + 1}`}
                />
                <button className="nav-btn prev" onClick={(e) => { e.stopPropagation(); prevImage(); }}>
                  &lt;
                </button>
                <button className="nav-btn next" onClick={(e) => { e.stopPropagation(); nextImage(); }}>
                  &gt;
                </button>
              </div>
              <div className="thumbnail-container">
                {selectedProject.gallery.map((img, index) => (
                  <div
                    key={index}
                    className={`thumbnail ${index === activeImageIndex ? 'active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setActiveImageIndex(index); }}
                  >
                    <img src={img} alt={`Thumbnail ${index + 1}`} />
                  </div>
                ))}
              </div>
            </div>

            <div className="details-section">
              <div className="details-column">
                <div className="detail-group">
                  <h3>Project Details</h3>
                  <div className="detail-item">
                    <strong>Client:</strong> {selectedProject.client}
                  </div>
                  <div className="detail-item">
                    <strong>Architect:</strong> {selectedProject.architect}
                  </div>
                  <div className="detail-item">
                    <strong>Status:</strong> {selectedProject.status}
                  </div>
                  <div className="detail-item">
                    <strong>Strategy:</strong> {selectedProject.strategy}
                  </div>
                </div>

                <div className="detail-group">
                  <h3>Design Highlights</h3>
                  <div className="highlight-tags">
                    {selectedProject.designHighlights.map((highlight, index) => (
                      <span key={index} className="highlight-tag">
                        {highlight}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="details-column">
                {selectedProject.details && Object.entries(selectedProject.details)
                  .filter(([key, value]) => key !== 'overview' && Array.isArray(value))
                  .map(([key, items]) => (
                    <div className="detail-group" key={key}>
                      <h3>{formatSectionTitle(key)}</h3>
                      <ul>
                        {items.map((item, index) => <li key={index}>{item}</li>)}
                      </ul>
                    </div>
                  ))}
              </div>
            </div>

            <div className="project-description">
              <h3>Project Overview</h3>
              <p>{selectedProject.description}</p>
              {selectedProject.details?.overview && (
                <p>{selectedProject.details.overview}</p>
              )}
            </div>
          </div>
        </div>
      )}

      <Chatbot />
    </>
  );
};

export default Portfolio;
